import {
  useRef,
  useState
} from "react";

import logo from
  "./Sai_Glass_logo_final_white_transparent.png";


/* =========================================================
   MIRROR OPTIONS
   ========================================================= */

function getSubTypes(type) {

  if (type === "LED") {
    return [
      "Border",
      "Backlit",
      "Both"
    ];
  }


  if (type === "framed") {
    return [
      "Fibre",
      "Aluminium"
    ];
  }


  return [];
}


/* =========================================================
   DISPLAY HELPERS
   ========================================================= */

function getMirrorTypeLabel(type) {

  const labels = {

    LED:
      "LED Mirror",

    framed:
      "Framed Mirror",

    frameless:
      "Frameless Mirror",

  };


  return labels[type] || type;
}


function getUnitLabel(unit) {

  return unit === "cm"
    ? "cm"
    : "inches";

}


/* =========================================================
   SESSION ID
   ========================================================= */

/*
  One Session ID is created when this
  React app instance loads.

  Example:
  SES-MD4K7X9P
*/

function generateSessionId() {

  const randomCode =
    Math.random()
      .toString(36)
      .substring(2, 10)
      .toUpperCase();


  return `SES-${randomCode}`;
}


/* =========================================================
   APP
   ========================================================= */

function App() {

  /* MIRROR INPUTS */

  const [
    height,
    setHeight
  ] = useState("");


  const [
    width,
    setWidth
  ] = useState("");


  const [
    unit,
    setUnit
  ] = useState(
    "inches"
  );


  const [
    type,
    setType
  ] = useState("");


  const [
    subType,
    setSubType
  ] = useState("");


  const [
    finish,
    setFinish
  ] = useState("");


  /* RESULT */

  const [
    result,
    setResult
  ] = useState(null);


  /* CUSTOMER */

  const [
    name,
    setName
  ] = useState("");


  const [
    phone,
    setPhone
  ] = useState("");


  const [
    showModal,
    setShowModal
  ] = useState(false);


  /* UI STATE */

  const [
    errors,
    setErrors
  ] = useState({});


  const [
    isSubmitting,
    setIsSubmitting
  ] = useState(false);


  const [
    submissionError,
    setSubmissionError
  ] = useState("");


  /* =======================================================
     ANALYTICS SESSION STATE
     ======================================================= */

  /*
    useRef is used so the Session ID
    remains unchanged while the customer
    uses the current app session.
  */

  const sessionIdRef =
    useRef(null);


  if (
    sessionIdRef.current === null
  ) {

    sessionIdRef.current =
      generateSessionId();

  }


  /*
    This stores the number that will be
    assigned to the NEXT successful
    estimate.

    First estimate = 1
  */

  const [
    nextEstimateSequence,
    setNextEstimateSequence
  ] = useState(1);


  /* =======================================================
     VALIDATE MIRROR INPUTS
     ======================================================= */

  const validateInputs = () => {

    const err = {};


    if (
      !height ||
      isNaN(height) ||
      Number(height) <= 0
    ) {

      err.height =
        "Enter valid positive height";

    }


    if (
      !width ||
      isNaN(width) ||
      Number(width) <= 0
    ) {

      err.width =
        "Enter valid positive width";

    }


    if (!type) {

      err.type =
        "Select mirror type";

    }


    if (
      (
        type === "LED" ||
        type === "framed"
      ) &&
      !subType
    ) {

      err.subType =
        "Select mirror subtype";

    }


    if (
      type === "framed" &&
      subType === "Aluminium" &&
      !finish
    ) {

      err.finish =
        "Select aluminium finish";

    }


    return err;
  };


  /* =======================================================
     VALIDATE CUSTOMER DETAILS
     ======================================================= */

  const validateLead = () => {

    const err = {};


    if (
      !name.trim()
    ) {

      err.name =
        "Name required";

    }


    if (
      !/^\d{10}$/
        .test(phone)
    ) {

      err.phone =
        "Enter valid 10-digit number";

    }


    return err;
  };


  /* =======================================================
     SUBMIT ENQUIRY
     ======================================================= */

  const handleCalculate =
    async () => {


      /* MIRROR VALIDATION */

      const inputErr =
        validateInputs();


      if (
        Object.keys(
          inputErr
        ).length > 0
      ) {

        setErrors(
          inputErr
        );

        return;
      }


      /*
        Ask for customer details only
        if they have not already been
        captured in this session.
      */

      if (
        !name ||
        !phone
      ) {

        setErrors({});

        setSubmissionError("");

        setShowModal(true);

        return;
      }


      /* CUSTOMER VALIDATION */

      const leadErr =
        validateLead();


      if (
        Object.keys(
          leadErr
        ).length > 0
      ) {

        setErrors(
          leadErr
        );

        return;
      }


      setIsSubmitting(true);

      setSubmissionError("");


      try {

        /*
          Send raw enquiry inputs together
          with the analytics session context.
        */

        const enquiryData = {

          sessionId:
            sessionIdRef.current,

          estimateSequence:
            nextEstimateSequence,

          name:
            name.trim(),

          phone,

          type,

          subType:
            subType || "",

          finish:
            finish || "",

          height:
            Number(height),

          width:
            Number(width),

          unit,

        };


        console.log(
          "Sending enquiry:",
          enquiryData
        );


        const response =
          await fetch(

            "https://mirror-price-enquiry-api.onrender.com/save-lead",

            {

              method:
                "POST",

              headers: {

                "Content-Type":
                  "application/json",

              },

              body:
                JSON.stringify(
                  enquiryData
                ),

            }

          );


        const data =
          await response
            .json();


        if (
          !response.ok ||
          !data.success
        ) {

          throw new Error(

            data.message ||
            "Unable to process enquiry"

          );

        }


        /*
          Store the successful estimate
          for the dedicated result view.
        */

        setResult({

          min:
            data.estimate.min,

          max:
            data.estimate.max,

          enquiryId:
            data.enquiryId,

          sessionId:
            data.sessionId,

          estimateSequence:
            data.estimateSequence,

          mirrorType:
            type,

          subType,

          finish,

          height,

          width,

          unit,

        });


        /*
          IMPORTANT:

          Increment only AFTER the backend
          and Google Sheet confirm success.

          If a request fails, the same
          sequence number can be retried.
        */

        setNextEstimateSequence(
          (
            currentSequence
          ) =>
            currentSequence + 1
        );


        setShowModal(false);

        setErrors({});


        console.log(

          "Enquiry saved successfully:",

          data.enquiryId,

          "Session:",

          data.sessionId,

          "Estimate:",

          data.estimateSequence

        );

      }

      catch (error) {

        console.error(

          "Submission error:",

          error

        );


        setSubmissionError(

          "We could not generate your estimate right now. Please try again."

        );

      }

      finally {

        setIsSubmitting(false);

      }

    };


  /* =======================================================
     CHECK ANOTHER ESTIMATE
     ======================================================= */

  const handleAnotherEstimate =
    () => {


      /*
        Keep:
        - Customer name
        - Customer phone
        - Session ID
        - Next estimate sequence

        Clear:
        - Previous mirror specifications
        - Previous result
      */

      setResult(null);

      setHeight("");

      setWidth("");

      setUnit(
        "inches"
      );

      setType("");

      setSubType("");

      setFinish("");

      setErrors({});

      setSubmissionError("");

    };


  /* =======================================================
     WHATSAPP LINK
     ======================================================= */

  const getWhatsAppLink =
    () => {


      if (!result) {

        return (
          "https://wa.me/919590576699"
        );

      }


      const mirrorDescription = [

        getMirrorTypeLabel(
          result.mirrorType
        ),

        result.subType,

        result.finish

      ]

        .filter(Boolean)

        .join(" - ");


      const message =

        `Hi Sai Glass, I received an estimated price of ` +

        `₹${result.min} - ₹${result.max} for a ` +

        `${mirrorDescription}, size ` +

        `${result.height} × ${result.width} ` +

        `${getUnitLabel(result.unit)}. ` +

        `My enquiry ID is ${result.enquiryId}. ` +

        `I would like to discuss the final price.`;


      return (

        `https://wa.me/919590576699?text=${

          encodeURIComponent(
            message
          )

        }`

      );

    };


  /* =======================================================
     UI
     ======================================================= */

  return (

    <div style={pageStyle}>

      <div style={cardStyle}>


        {/* ================================================
            ENQUIRY FORM VIEW
        ================================================ */}

        {!result && (

          <>

            <img

              src={logo}

              alt="SaiGlass Logo"

              style={logoStyle}

            />


            <h2 style={headingStyle}>

              Mirror Design Estimator

            </h2>


            <select

              style={fieldStyle}

              value={unit}

              onChange={(e) => {

                setUnit(
                  e.target.value
                );

                setSubmissionError("");

              }}

            >

              <option value="inches">

                Inches

              </option>


              <option value="cm">

                Centimeters

              </option>

            </select>


            <input

              type="number"

              style={fieldStyle}

              placeholder={
                `Height (${unit})`
              }

              value={height}

              onChange={(e) => {

                setHeight(
                  e.target.value
                );

                setSubmissionError("");

              }}

            />


            {errors.height && (

              <p style={errorStyle}>

                {errors.height}

              </p>

            )}


            <input

              type="number"

              style={fieldStyle}

              placeholder={
                `Width (${unit})`
              }

              value={width}

              onChange={(e) => {

                setWidth(
                  e.target.value
                );

                setSubmissionError("");

              }}

            />


            {errors.width && (

              <p style={errorStyle}>

                {errors.width}

              </p>

            )}


            <select

              style={fieldStyle}

              value={type}

              onChange={(e) => {

                setType(
                  e.target.value
                );

                setSubType("");

                setFinish("");

                setSubmissionError("");

              }}

            >

              <option value="">

                Select Mirror Type

              </option>


              <option value="LED">

                LED Mirror

              </option>


              <option value="framed">

                Framed Mirror

              </option>


              <option value="frameless">

                Frameless Mirror

              </option>

            </select>


            {errors.type && (

              <p style={errorStyle}>

                {errors.type}

              </p>

            )}


            {getSubTypes(type).length > 0 && (

              <>

                <select

                  style={fieldStyle}

                  value={subType}

                  onChange={(e) => {

                    setSubType(
                      e.target.value
                    );

                    setFinish("");

                    setSubmissionError("");

                  }}

                >

                  <option value="">

                    Select Type

                  </option>


                  {getSubTypes(type).map(

                    (s) => (

                      <option

                        key={s}

                        value={s}

                      >

                        {s}

                      </option>

                    )

                  )}

                </select>


                {errors.subType && (

                  <p style={errorStyle}>

                    {errors.subType}

                  </p>

                )}

              </>

            )}


            {
              type === "framed" &&
              subType === "Aluminium" && (

                <>

                  <select

                    style={fieldStyle}

                    value={finish}

                    onChange={(e) => {

                      setFinish(
                        e.target.value
                      );

                      setSubmissionError("");

                    }}

                  >

                    <option value="">

                      Select Finish

                    </option>


                    <option value="rosegold">

                      Rose Gold

                    </option>


                    <option value="gold">

                      Gold

                    </option>


                    <option value="silver">

                      Silver

                    </option>


                    <option value="black">

                      Black

                    </option>

                  </select>


                  {errors.finish && (

                    <p style={errorStyle}>

                      {errors.finish}

                    </p>

                  )}

                </>

              )
            }


            <p style={privacyStyle}>

              We respect your privacy. No spam calls.

            </p>


            <button

              style={buttonStyle}

              onClick={
                handleCalculate
              }

              disabled={
                isSubmitting
              }

            >

              {
                isSubmitting

                  ? "Generating Estimate..."

                  : "Get Estimate"
              }

            </button>


            {
              submissionError &&
              !showModal && (

                <p style={errorStyle}>

                  {submissionError}

                </p>

              )
            }

          </>

        )}


        {/* ================================================
            RESULT VIEW
        ================================================ */}

        {result && (

          <div style={resultViewStyle}>


            <img

              src={logo}

              alt="SaiGlass Logo"

              style={resultLogoStyle}

            />


            <p style={successLabelStyle}>

              ESTIMATE READY

            </p>


            <h2 style={resultHeadingStyle}>

              Your Estimated Price

            </h2>


            <div style={priceBoxStyle}>

              <div style={priceStyle}>

                ₹{
                  result.min
                    .toLocaleString(
                      "en-IN"
                    )
                }

                {" – "}

                ₹{
                  result.max
                    .toLocaleString(
                      "en-IN"
                    )
                }

              </div>

            </div>


            <div style={specificationBoxStyle}>

              <p style={specificationTitleStyle}>

                Estimate for

              </p>


              <p style={mirrorDescriptionStyle}>

                {
                  getMirrorTypeLabel(
                    result.mirrorType
                  )
                }


                {
                  result.subType &&
                  ` • ${result.subType}`
                }


                {
                  result.finish &&
                  ` • ${result.finish}`
                }

              </p>


              <p style={dimensionStyle}>

                {result.height}

                {" × "}

                {result.width}

                {" "}

                {
                  getUnitLabel(
                    result.unit
                  )
                }

              </p>

            </div>


            <p style={disclaimerStyle}>

              This is an indicative estimate. Final
              price may vary based on installation,
              site conditions and customization.

            </p>


            <a

              href="tel:+919590576699"

              style={linkStyle}

            >

              <button style={callButtonStyle}>

                📞 Call Now for Best Price

              </button>

            </a>


            <a

              href={
                getWhatsAppLink()
              }

              target="_blank"

              rel="noreferrer"

              style={linkStyle}

            >

              <button style={whatsappButtonStyle}>

                💬 Chat on WhatsApp

              </button>

            </a>


            <button

              style={anotherEstimateButtonStyle}

              onClick={
                handleAnotherEstimate
              }

            >

              ← Check Another Estimate

            </button>


            <p style={enquiryIdStyle}>

              Enquiry ID: {
                result.enquiryId
              }

            </p>

          </div>

        )}

      </div>


      {/* ================================================
          CUSTOMER DETAILS MODAL
      ================================================ */}

      {showModal && (

        <div style={overlay}>

          <div style={modal}>


            <h3 style={modalHeadingStyle}>

              Enter your details

            </h3>


            <p style={modalDescriptionStyle}>

              Enter your contact details to view
              your estimated mirror price.

            </p>


            <input

              style={modalInput}

              placeholder="Your Name"

              value={name}

              onChange={(e) =>

                setName(
                  e.target.value
                )

              }

            />


            {errors.name && (

              <p style={errorStyle}>

                {errors.name}

              </p>

            )}


            <input

              type="tel"

              style={modalInput}

              placeholder="Phone Number"

              value={phone}

              maxLength={10}

              onChange={(e) =>

                setPhone(

                  e.target.value
                    .replace(
                      /\D/g,
                      ""
                    )

                )

              }

            />


            {errors.phone && (

              <p style={errorStyle}>

                {errors.phone}

              </p>

            )}


            {submissionError && (

              <p style={errorStyle}>

                {submissionError}

              </p>

            )}


            <button

              style={modalBtn}

              onClick={
                handleCalculate
              }

              disabled={
                isSubmitting
              }

            >

              {
                isSubmitting

                  ? "Generating Estimate..."

                  : "Submit & Get Price"
              }

            </button>

          </div>

        </div>

      )}

    </div>

  );

}


/* =========================================================
   STYLES
   ========================================================= */

const pageStyle = {

  minHeight: "100vh",

  background: "#f5f7fa",

  display: "flex",

  justifyContent: "center",

  alignItems: "center",

  padding: "20px",

  boxSizing: "border-box",

};


const cardStyle = {

  width: "100%",

  maxWidth: "420px",

  background: "white",

  padding: "30px",

  borderRadius: "12px",

  textAlign: "center",

  boxSizing: "border-box",

  boxShadow:
    "0 10px 30px rgba(0, 0, 0, 0.06)",

};


const headingStyle = {

  marginTop: "10px",

  marginBottom: "20px",

};


const fieldStyle = {

  width: "100%",

  padding: "12px",

  marginTop: "12px",

  borderRadius: "8px",

  border:
    "1px solid #ccc",

  fontSize: "14px",

  boxSizing: "border-box",

  height: "44px",

};


const privacyStyle = {

  fontSize: "12px",

  color: "#666",

  marginTop: "14px",

};


const buttonStyle = {

  width: "100%",

  marginTop: "12px",

  padding: "14px",

  background:
    "linear-gradient(135deg, #1e3a8a, #2563eb)",

  color: "white",

  border: "none",

  borderRadius: "8px",

  fontWeight: "bold",

  cursor: "pointer",

};


/* RESULT */

const resultViewStyle = {

  width: "100%",

};


const resultLogoStyle = {

  width: "105px",

  display: "block",

  margin:
    "0 auto 15px",

};


const successLabelStyle = {

  fontSize: "12px",

  fontWeight: "bold",

  letterSpacing: "1.5px",

  color: "#16a34a",

  marginBottom: "8px",

};


const resultHeadingStyle = {

  margin:
    "0 0 18px",

  fontSize: "24px",

};


const priceBoxStyle = {

  padding:
    "22px 12px",

  background:
    "#f5f7fa",

  borderRadius: "12px",

  marginBottom: "18px",

};


const priceStyle = {

  fontSize: "30px",

  fontWeight: "bold",

  color: "#111827",

};


const specificationBoxStyle = {

  padding: "15px",

  border:
    "1px solid #e5e7eb",

  borderRadius: "10px",

  marginBottom: "18px",

};


const specificationTitleStyle = {

  margin:
    "0 0 6px",

  fontSize: "12px",

  color: "#6b7280",

};


const mirrorDescriptionStyle = {

  margin: "0",

  fontWeight: "bold",

  fontSize: "16px",

};


const dimensionStyle = {

  margin:
    "7px 0 0",

  color: "#4b5563",

};


const disclaimerStyle = {

  color: "#666",

  fontSize: "13px",

  lineHeight: "1.5",

  marginBottom: "18px",

};


const linkStyle = {

  textDecoration: "none",

};


const callButtonStyle = {

  width: "100%",

  padding: "14px",

  background: "#16a34a",

  color: "white",

  border: "none",

  borderRadius: "8px",

  fontWeight: "bold",

  cursor: "pointer",

};


const whatsappButtonStyle = {

  width: "100%",

  marginTop: "10px",

  padding: "14px",

  background: "#25D366",

  color: "white",

  border: "none",

  borderRadius: "8px",

  fontWeight: "bold",

  cursor: "pointer",

};


const anotherEstimateButtonStyle = {

  width: "100%",

  marginTop: "12px",

  padding: "13px",

  background: "white",

  color: "#2563eb",

  border:
    "1px solid #2563eb",

  borderRadius: "8px",

  fontWeight: "bold",

  cursor: "pointer",

};


const enquiryIdStyle = {

  marginTop: "18px",

  marginBottom: "0",

  fontSize: "11px",

  color: "#9ca3af",

};


/* LOGO */

const logoStyle = {

  width: "120px",

  display: "block",

  margin:
    "0 auto 10px",

};


/* MODAL */

const overlay = {

  position: "fixed",

  top: 0,

  left: 0,

  width: "100%",

  height: "100%",

  background:
    "rgba(0,0,0,0.5)",

  display: "flex",

  justifyContent: "center",

  alignItems: "center",

  padding: "20px",

  boxSizing: "border-box",

  zIndex: 1000,

};


const modal = {

  background: "white",

  padding: "25px",

  borderRadius: "10px",

  width: "100%",

  maxWidth: "320px",

  display: "flex",

  flexDirection: "column",

  gap: "10px",

  boxSizing: "border-box",

};


const modalHeadingStyle = {

  margin: "0",

};


const modalDescriptionStyle = {

  fontSize: "13px",

  color: "#666",

  lineHeight: "1.5",

  marginTop: "0",

};


const modalInput = {

  padding: "12px",

  borderRadius: "6px",

  border:
    "1px solid #ccc",

};


const modalBtn = {

  padding: "12px",

  background:
    "linear-gradient(135deg, #1e3a8a, #2563eb)",

  color: "white",

  border: "none",

  borderRadius: "6px",

  cursor: "pointer",

  fontWeight: "bold",

};


const errorStyle = {

  color: "red",

  fontSize: "12px",

  margin:
    "4px 0",

};


export default App;